import axios from 'axios';
import endpoints from './endpoints';
import { STORAGE_KEYS } from '../context/AuthContext';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});
// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const isUserAnonymous = () => {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    if (!storedUser) return false;
    try {
        const user = JSON.parse(storedUser);
        return user.is_anonymous || false;
    } catch (error) {
        console.error('Error parsing user data:', error);
        return false;
    }
};

// Add request interceptor to include token if available
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (config.url === endpoints.auth.login ||
            config.url === endpoints.auth.googleLogin) {
            const anonymous = isUserAnonymous();
            if (anonymous && config.data) {
                const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
                config.data = {
                    ...data,
                    link_anonymous: true
                };
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Skip token refresh for Google login endpoint
        if (originalRequest.url === endpoints.auth.googleLogin) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
                    null,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Refresh-Token': refreshToken
                        }
                    }
                );

                // Update to match API response structure
                const { access_token: newToken, refresh_token: newRefreshToken } = response.data;

                if (!newToken || !newRefreshToken) {
                    throw new Error('Invalid token response from server');
                }

                localStorage.setItem(STORAGE_KEYS.TOKEN, newToken);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);

                // Update Authorization header for all future requests
                api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                processQueue(null, newToken);
                return api(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                processQueue(refreshError, null);

                // Clear auth data and redirect to login
                localStorage.removeItem(STORAGE_KEYS.TOKEN);
                localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
                localStorage.removeItem(STORAGE_KEYS.USER);
                window.location.href = '/login';

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default api;

